import { canListingTransition, type Listing, type ListingStatus } from '@carsfinders/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { ConfirmDialog } from '../../components/confirm-dialog';
import { t } from '../../lib/i18n';
import { toast } from '../../lib/toast';
import {
  fetchListingAnalytics,
  fetchMyListings,
  fetchSalesCount,
  submitSellerListing,
} from './api';
import { STATUS_CHIP } from './moderation-queue-tab';
import { SellerListingForm } from './seller-listing-form';
import { computeNextSaleBracket } from './seller-tax';

/** Statuses the owner may still edit in (mirrors the API's EDITABLE_STATUSES). */
const EDITABLE: readonly ListingStatus[] = ['draft', 'rejected'];

/** The four steps a consignment listing walks — the bar under every row. */
const STEPS = ['draft', 'moderation', 'active', 'sold'] as const;

/**
 * Where each status sits on the bar, and the colour of its current step:
 * `rejected` is stopped AT moderation, `reserved` is still on the active step,
 * and `archived` is off the bar entirely.
 */
const STEP_FOR_STATUS: Record<ListingStatus, { index: number; bar: string }> = {
  draft: { index: 0, bar: 'bg-cf-ink-subtle' },
  moderation: { index: 1, bar: 'bg-cf-accent' },
  rejected: { index: 1, bar: 'bg-cf-danger' },
  active: { index: 2, bar: 'bg-cf-success' },
  reserved: { index: 2, bar: 'bg-cf-accent' },
  sold: { index: 3, bar: 'bg-cf-info' },
  archived: { index: -1, bar: 'bg-cf-line' },
};

const SECONDARY_BUTTON =
  'inline-flex h-9 items-center gap-1.5 rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[13px] font-semibold text-cf-ink hover:border-cf-ink';

const MY_LISTINGS_KEY = ['marketplace-seller-listings'] as const;

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(price);
  } catch {
    return `${price.toLocaleString('de-CH')} ${currency}`;
  }
}

/**
 * «Мої оголошення» — the marketplace seller cabinet (`market…/sell`), moved
 * into the admin while the public cabinet is hidden (owner decision
 * 2026-10-10). The admin creates consignment listings in their OWN name through
 * the seller routes, edits a draft/rejected one, reads its analytics, and
 * submits a draft for moderation behind the PIT-ladder warning — after which it
 * lands in the «Черга модерації» tab like any client's listing.
 */
export function SellerListingsTab() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Listing | null>(null);
  /** Listing awaiting the tax-warning confirmation before it is submitted. */
  const [pendingSubmit, setPendingSubmit] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const listingsQuery = useQuery({ queryKey: MY_LISTINGS_KEY, queryFn: fetchMyListings });
  const salesQuery = useQuery({ queryKey: ['marketplace-sales-count'], queryFn: fetchSalesCount });

  const submitMutation = useMutation({
    mutationFn: submitSellerListing,
    onSuccess: () => {
      toast.success(t('marketplace.admin.seller.submitSuccess'));
      void queryClient.invalidateQueries({ queryKey: MY_LISTINGS_KEY });
      // The listing is now in `moderation` — the queue tab's feed.
      void queryClient.invalidateQueries({ queryKey: ['admin-marketplace-listings'] });
    },
    onError: (error: unknown) =>
      toast.error(
        t('marketplace.admin.seller.submitError', {
          error: error instanceof Error ? error.message : 'unknown',
        }),
      ),
    onSettled: () => setPendingSubmit(null),
  });

  const listings = listingsQuery.data ?? [];
  const inModeration = listings.filter((listing) => listing.status === 'moderation').length;
  // The server's sold count picks the bracket; the confirm stays disabled until
  // it answers, so the 0% first-sale bracket is never shown as a guess.
  const bracket = computeNextSaleBracket(salesQuery.data ?? 0);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  return (
    <>
      <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-3.5 lg:px-5 lg:py-[18px]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[17px]">
              {t('marketplace.seller.cabinet.myListings')}
            </h2>
            <p className="mt-1 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase">
              {t('marketplace.seller.cabinet.eyebrow', {
                count: listings.length,
                pending: inModeration,
              })}
            </p>
          </div>
          {!formOpen && (
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              data-testid="seller-new-listing"
              className="inline-flex h-10 items-center gap-2 rounded-cf-md bg-cf-brand px-4 text-sm font-semibold text-white hover:bg-cf-brand-hover"
            >
              <Plus size={16} aria-hidden />
              {t('marketplace.seller.cabinet.newListing')}
            </button>
          )}
        </div>
        <p className="mt-2 text-xs leading-[1.4] text-cf-ink-subtle lg:text-[13px]">
          {t('marketplace.admin.seller.intro')}
        </p>
      </section>

      {formOpen && (
        <SellerListingForm
          // Remount per target so another draft is read afresh.
          key={editing?.listingId ?? 'new'}
          editing={editing}
          onSaved={() => {
            closeForm();
            void queryClient.invalidateQueries({ queryKey: MY_LISTINGS_KEY });
          }}
          onCancel={closeForm}
        />
      )}

      {listingsQuery.isPending ? (
        <p className="text-[13px] text-cf-ink-subtle" data-testid="seller-loading">
          {t('marketplace.seller.cabinet.loading')}
        </p>
      ) : listingsQuery.isError ? (
        <div>
          <p className="text-[13px] text-cf-danger" role="alert" data-testid="seller-load-error">
            {t('marketplace.seller.cabinet.loadError')}
          </p>
          <button
            type="button"
            onClick={() => void listingsQuery.refetch()}
            className="mt-2 text-[13px] font-medium text-cf-brand hover:underline"
          >
            {t('marketplace.seller.cabinet.retry')}
          </button>
        </div>
      ) : listings.length === 0 ? (
        <p
          className="rounded-cf-lg border border-dashed border-cf-line bg-cf-surface px-4 py-8 text-center text-[13px] text-cf-ink-muted"
          data-testid="seller-empty"
        >
          {t('marketplace.seller.cabinet.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="seller-listings">
          {listings.map((listing) => {
            const isOpen = expanded === listing.listingId;
            const canSubmit = canListingTransition(listing.status, 'moderation').ok;
            return (
              <SellerListingRow
                key={listing.listingId}
                listing={listing}
                panel={isOpen ? <ListingAnalyticsPanel listingId={listing.listingId} /> : null}
                actions={
                  <>
                    {EDITABLE.includes(listing.status) && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(listing);
                          setFormOpen(true);
                        }}
                        className={SECONDARY_BUTTON}
                        data-testid={`seller-edit-${listing.listingId}`}
                      >
                        {t('marketplace.seller.actions.edit')}
                      </button>
                    )}
                    {/* rejected → moderation is not a ladder step: a rejected listing goes back to draft first. */}
                    {canSubmit && (
                      <button
                        type="button"
                        onClick={() => setPendingSubmit(listing.listingId)}
                        className="inline-flex h-9 items-center rounded-cf-md bg-cf-brand px-3 text-[13px] font-semibold text-white hover:bg-cf-brand-hover"
                        data-testid={`seller-submit-${listing.listingId}`}
                      >
                        {t('marketplace.seller.actions.submit')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : listing.listingId)}
                      aria-expanded={isOpen}
                      className="ml-auto inline-flex h-9 items-center gap-1 px-1 text-[13px] text-cf-ink-muted hover:text-cf-ink"
                      data-testid={`seller-analytics-toggle-${listing.listingId}`}
                    >
                      {isOpen ? (
                        <ChevronUp size={16} aria-hidden />
                      ) : (
                        <ChevronDown size={16} aria-hidden />
                      )}
                      {t('marketplace.seller.analytics.title')}
                    </button>
                  </>
                }
              />
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={pendingSubmit !== null}
        title={t('marketplace.seller.tax.title')}
        confirmLabel={
          submitMutation.isPending
            ? t('marketplace.seller.actions.submitting')
            : t('marketplace.seller.tax.proceed')
        }
        cancelLabel={t('marketplace.seller.tax.cancel')}
        busy={submitMutation.isPending || salesQuery.isPending}
        onConfirm={() => {
          if (pendingSubmit) submitMutation.mutate(pendingSubmit);
        }}
        onCancel={() => setPendingSubmit(null)}
        message={
          <div data-testid="tax-warning">
            <p>{t('marketplace.seller.tax.ordinal', { n: bracket.ordinal })}</p>
            <p
              className={`mt-3 rounded-cf-md p-3 ${
                bracket.totalPct === 0
                  ? 'bg-cf-success-tint text-cf-success'
                  : 'bg-cf-warn-tint text-cf-warn'
              }`}
              data-testid={`tax-warning-bracket-${bracket.key}`}
            >
              {t(`marketplace.seller.tax.${bracket.key}`)}
            </p>
            <div className="mt-3 flex justify-between gap-2">
              <span className="text-cf-ink-subtle">{t('marketplace.seller.tax.rateLabel')}</span>
              <span className="font-mono font-semibold text-cf-ink">
                {t('marketplace.seller.tax.total', { pct: bracket.totalPct })}
              </span>
            </div>
            <div className="flex justify-between gap-2 text-[13px]">
              <span>{t('marketplace.seller.tax.pit', { pct: bracket.pitPct })}</span>
              <span>{t('marketplace.seller.tax.military', { pct: bracket.militaryPct })}</span>
            </div>
            <p className="mt-3 text-xs text-cf-ink-subtle">
              {t('marketplace.seller.tax.disclaimer')}
            </p>
          </div>
        }
      />
    </>
  );
}

/** One listing: photo, status chip, title, year · mileage, price, progress bar, actions. */
function SellerListingRow({
  listing,
  actions,
  panel,
}: {
  listing: Listing;
  actions: ReactNode;
  /** Expanded content under the row (the analytics panel). */
  panel: ReactNode;
}) {
  const step = STEP_FOR_STATUS[listing.status];
  const photo = listing.photos?.[0];
  const meta = [String(listing.year), `${listing.mileage.toLocaleString('de-CH')} km`].join(' · ');

  return (
    <li
      className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface lg:grid-cols-[160px_minmax(0,1fr)] lg:gap-x-0"
      data-testid={`seller-listing-${listing.listingId}`}
    >
      <div className="relative mt-3 ml-3 aspect-[4/3] overflow-hidden rounded-cf-md bg-cf-fill lg:row-span-3 lg:m-0 lg:aspect-auto lg:min-h-[120px] lg:rounded-none">
        {photo && (
          <img
            src={photo}
            alt={listing.title}
            loading="lazy"
            className="absolute inset-0 block h-full w-full object-cover"
          />
        )}
      </div>

      <div className="mt-3 flex min-w-0 flex-col gap-1 pr-3.5 lg:mt-4 lg:flex-row lg:items-start lg:gap-6 lg:pr-5 lg:pl-5">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span
            className={`inline-flex w-fit rounded-cf-sm px-2 py-1 font-mono text-[10px] tracking-[0.08em] uppercase ${STATUS_CHIP[listing.status]}`}
            data-testid={`seller-status-${listing.listingId}`}
          >
            {t(`marketplace.seller.status.${listing.status}`)}
          </span>
          <h3 className="truncate text-[15px] font-semibold tracking-[-0.01em] text-cf-ink lg:text-[17px]">
            {listing.title}
          </h3>
          <p className="truncate text-xs text-cf-ink-subtle">{meta}</p>
        </div>
        <p className="shrink-0 font-mono text-[13px] font-medium text-cf-ink lg:text-[20px]">
          {formatPrice(listing.price, listing.currency)}
        </p>
      </div>

      <ol
        className="col-span-2 mt-3 grid grid-cols-4 gap-1 px-3.5 lg:col-span-1 lg:col-start-2 lg:mx-5 lg:gap-1.5 lg:px-0"
        aria-label={t('marketplace.seller.progress.label')}
      >
        {STEPS.map((key, index) => {
          const isCurrent = index === step.index;
          const bar =
            index < step.index ? 'bg-cf-success' : isCurrent ? step.bar : 'bg-cf-line-soft';
          return (
            <li
              key={key}
              className="flex min-w-0 flex-col gap-1"
              aria-current={isCurrent ? 'step' : undefined}
            >
              <span aria-hidden className={`h-1 rounded-cf-xs ${bar}`} />
              <span
                className={`truncate text-[10px] lg:text-[11px] ${
                  index <= step.index ? 'text-cf-ink' : 'text-cf-ink-subtle'
                } ${isCurrent ? 'font-semibold' : 'font-normal'}`}
              >
                {t(`marketplace.seller.progress.${key}`)}
              </span>
            </li>
          );
        })}
      </ol>

      <div className="col-span-2 mt-3 flex flex-wrap items-center gap-2 border-t border-cf-line-soft px-3.5 py-2.5 lg:col-span-1 lg:col-start-2 lg:mx-5 lg:mb-4 lg:px-0 lg:pb-0">
        {actions}
      </div>

      {panel && <div className="col-span-2 border-t border-cf-line-soft p-3.5 lg:p-5">{panel}</div>}
    </li>
  );
}

/** Views, saves, leads by channel and days on market — owner|admin scoped. */
function ListingAnalyticsPanel({ listingId }: { listingId: string }) {
  const query = useQuery({
    queryKey: ['marketplace-listing-analytics', listingId],
    queryFn: () => fetchListingAnalytics(listingId),
  });

  if (query.isPending) {
    return (
      <p className="text-[13px] text-cf-ink-subtle">{t('marketplace.seller.analytics.loading')}</p>
    );
  }
  if (query.isError) {
    return (
      <p className="text-[13px] text-cf-ink-subtle">{t('marketplace.seller.analytics.error')}</p>
    );
  }

  const data = query.data;
  const tiles = [
    { key: 'views', value: String(data.views) },
    { key: 'saves', value: String(data.saves) },
    { key: 'leads', value: String(data.leadCount) },
    // null = never published, so there is no days-on-market anchor yet.
    { key: 'daysOnMarket', value: data.daysOnMarket === null ? '—' : String(data.daysOnMarket) },
  ];
  const channels = Object.entries(data.leadsByChannel).filter(([, count]) => (count ?? 0) > 0);

  return (
    <section aria-label={t('marketplace.seller.analytics.title')} data-testid="listing-analytics">
      <h4 className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
        <BarChart3 size={16} className="text-cf-brand" aria-hidden />
        {t('marketplace.seller.analytics.title')}
      </h4>
      <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {tiles.map((tile) => (
          <div
            key={tile.key}
            className="rounded-cf-md border border-cf-line bg-cf-surface-sunken p-3"
          >
            <dd
              className="font-mono text-[20px] leading-[1.1] font-medium"
              data-testid={`analytics-${tile.key}`}
            >
              {tile.value}
            </dd>
            <dt className="mt-1 font-mono text-[10px] tracking-[0.08em] text-cf-ink-subtle uppercase">
              {t(`marketplace.seller.analytics.${tile.key}`)}
            </dt>
          </div>
        ))}
      </dl>
      {channels.length > 0 && (
        <p className="mt-3 text-xs text-cf-ink-muted">
          {t('marketplace.seller.analytics.byChannel')}{' '}
          {channels
            .map(
              ([channel, count]) =>
                `${t(`marketplace.seller.analytics.channel.${channel}`)}: ${count}`,
            )
            .join(' · ')}
        </p>
      )}
    </section>
  );
}
